import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getQuote, createCheckoutSession } from "@/lib/api";
import { toast } from "sonner";
import { 
  Truck, 
  MapPin, 
  Package, 
  Clock,
  CreditCard,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  FileImage
} from "lucide-react";

export default function ViewQuotePage() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const data = await getQuote(quoteId);
        setQuote(data);
      } catch (error) {
        console.error("Error fetching quote:", error);
        toast.error("Failed to load quote");
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [quoteId]);

  const handlePayment = async () => {
    setPaying(true);
    try {
      const { checkout_url } = await createCheckoutSession(quoteId);
      window.location.href = checkout_url;
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setPaying(false);
    }
  };

  const buildAddressLines = (address, fallbackPostcode) => {
    if (!address && !fallbackPostcode) {
      return [];
    }
    const lines = [];
    if (address?.company_name) {
      lines.push(address.company_name);
    }
    const contactLine = [address?.contact_name, address?.contact_phone].filter(Boolean).join(" — ");
    if (contactLine) {
      lines.push(contactLine);
    }
    if (address?.address_line_1) {
      lines.push(address.address_line_1);
    }
    if (address?.address_line_2) {
      lines.push(address.address_line_2);
    }
    const cityLine = [address?.city, address?.county].filter(Boolean).join(", ");
    if (cityLine) {
      lines.push(cityLine);
    }
    if (address?.postcode) {
      lines.push(address.postcode);
    }
    if (address?.country) {
      lines.push(address.country);
    }
    if (!lines.length && fallbackPostcode) {
      lines.push(fallbackPostcode);
    }
    return lines;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#22c55e] animate-spin" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Quote Not Found</h1>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isPaid = quote.status === "paid" || quote.status === "collected" || quote.status === "delivered";
  const hasQuote = quote.quoted_price !== null;

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#22c55e] rounded-sm flex items-center justify-center">
              <Truck className="w-6 h-6 text-[#020617]" />
            </div>
            <div className="flex flex-col">
              <span className="font-['Barlow_Condensed'] text-xl font-bold text-white leading-tight">
                RTS EXPRESS
              </span>
              <span className="text-[10px] text-slate-400 tracking-wide">SAME DAY COURIER</span>
            </div>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Status Badge */}
        <div className="mb-6">
          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium badge-${quote.status}`}>
            {quote.status === "delivered" && <CheckCircle2 className="w-4 h-4" />}
            {quote.status.toUpperCase()}
          </span>
        </div>

        <h1 className="font-['Barlow_Condensed'] text-4xl font-bold text-white mb-2">
          {isPaid ? "BOOKING" : hasQuote ? "YOUR QUOTE" : "QUOTE PENDING"}
        </h1>
        <p className="text-slate-400 mb-8">
          Reference: <span className="font-mono text-white">{quoteId}</span>
        </p>

        {/* Quote Price */}
        {hasQuote && (
          <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-8 mb-6">
            {isPaid ? (
              <div className="flex items-center justify-center gap-3 text-[#22c55e]">
                <CheckCircle2 className="w-8 h-8" />
                <span className="font-['Barlow_Condensed'] text-3xl font-bold">PAYMENT CONFIRMED</span>
              </div>
            ) : (
              <>
                <div className="space-y-3 max-w-xs mx-auto">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Net (ex VAT):</span>
                    <span className="text-white">£{(quote.quoted_price_net || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">VAT (20%):</span>
                    <span className="text-white">£{(quote.quoted_price_vat || 0).toFixed(2)}</span>
                  </div>
                  <div className="border-t border-slate-700 pt-3 text-center">
                    <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Total to Pay</p>
                    <p className="font-['Barlow_Condensed'] text-5xl font-black text-[#22c55e]">
                      £{quote.quoted_price?.toFixed(2)}
                    </p>
                    <p className="text-slate-500 text-xs mt-2">Inc. VAT</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Waiting for Quote */}
        {!hasQuote && (
          <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-8 mb-6 text-center">
            <Clock className="w-12 h-12 text-[#f59e0b] mx-auto mb-4" />
            <p className="font-['Barlow_Condensed'] text-2xl font-bold text-white mb-2">
              QUOTE IN PROGRESS
            </p>
            <p className="text-slate-400 text-sm">
              Our team is reviewing your request. You'll receive an email with your quote shortly.
            </p>
          </div>
        )}

        {/* Route Details */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 mb-6">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4">
            Route Details
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pickup Address */}
            <div className="bg-slate-900/50 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[#22c55e]/10 rounded-full flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#22c55e]" />
                </div>
                <p className="text-[#22c55e] font-bold uppercase text-xs tracking-wide">Pickup</p>
              </div>
              {quote.pickup_address ? (
                <div className="space-y-1 text-sm" data-testid="view-quote-pickup-address">
                  {buildAddressLines(quote.pickup_address, quote.pickup_postcode).map((line, index) => (
                    <p key={`pickup-${index}`} className={index === 0 ? "text-white font-semibold" : "text-slate-300"}>
                      {line}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-white font-semibold" data-testid="view-quote-pickup-postcode">{quote.pickup_postcode}</p>
              )}
            </div>

            {/* Delivery Address */}
            <div className="bg-slate-900/50 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[#3b82f6]/10 rounded-full flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#3b82f6]" />
                </div>
                <p className="text-[#3b82f6] font-bold uppercase text-xs tracking-wide">Delivery</p>
              </div>
              {quote.delivery_address ? (
                <div className="space-y-1 text-sm" data-testid="view-quote-delivery-address">
                  {buildAddressLines(quote.delivery_address, quote.delivery_postcode).map((line, index) => (
                    <p key={`delivery-${index}`} className={index === 0 ? "text-white font-semibold" : "text-slate-300"}>
                      {line}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-white font-semibold" data-testid="view-quote-delivery-postcode">{quote.delivery_postcode}</p>
              )}
            </div>
          </div>

        </div>

        {/* Cargo Details */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 mb-6">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4">
            Cargo Details
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Type</p>
              <p className="text-white capitalize flex items-center gap-2">
                <Package className="w-4 h-4 text-[#f59e0b]" />
                {quote.cargo_type}
                {quote.pallet_count && ` (${quote.pallet_count} pallet${quote.pallet_count > 1 ? 's' : ''})`}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Weight</p>
              <p className="text-white">{quote.total_weight_kg} kg</p>
            </div>
            {quote.dimensions && (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Dimensions</p>
                <p className="text-white">{quote.dimensions}</p>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Urgency</p>
              <p className="text-white capitalize">{quote.urgency.replace('_', ' ')}</p>
            </div>
          </div>
        </div>

        {/* Timing */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 mb-6">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4">
            Timing
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Ready for Collection</p>
              <p className="text-white">{new Date(quote.ready_time).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Delivery Deadline</p>
              <p className="text-white">{new Date(quote.delivery_deadline).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* POD Image (if delivered) */}
        {quote.status === "delivered" && quote.pod_image && (
          <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 mb-6">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FileImage className="w-5 h-5 text-[#22c55e]" />
              Proof of Delivery
            </h2>
            <img 
              src={quote.pod_image} 
              alt="Proof of Delivery" 
              className="w-full rounded-sm border border-slate-800"
            />
          </div>
        )}

        {/* Payment Button */}
        {hasQuote && !isPaid && (
          <>
            {/* B2B Legal Block */}
            <div className="bg-slate-900 border border-slate-700 rounded-sm p-5 mb-6">
              <h3 className="text-white font-bold text-sm uppercase tracking-wide mb-3">Important – B2B Service Only</h3>
              <p className="text-slate-300 text-sm mb-3">Payment is required before dispatch.</p>
              <p className="text-slate-400 text-sm mb-2">By completing this payment you:</p>
              <ul className="text-slate-400 text-sm space-y-1 mb-3 pl-4">
                <li>• Confirm acceptance of the quotation in full</li>
                <li>• Confirm you are acting on behalf of a registered business entity</li>
                <li>• Agree to our <Link to="/terms-and-conditions" className="text-[#22c55e] hover:underline">Terms & Conditions</Link></li>
                <li>• Enter into a legally binding B2B contract upon successful payment confirmation</li>
              </ul>
              <p className="text-[#f59e0b] text-sm font-semibold">No payment, no dispatch.</p>
            </div>
            <Button
              onClick={handlePayment}
              disabled={paying}
              className="w-full bg-[#22c55e] text-[#020617] hover:bg-[#22c55e]/90 font-bold text-lg py-6 h-auto animate-pulse-glow"
              data-testid="pay-now-btn"
            >
              {paying ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Redirecting to Payment...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-5 w-5" />
                  Pay £{quote.quoted_price?.toFixed(2)} Now
                </>
              )}
            </Button>
          </>
        )}

        {/* Contact Section for Paid Orders */}
        {isPaid && quote.whatsapp_visible && (
          <div className="bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-sm p-6 text-center">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-2">
              Contact RTS Dispatch
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Our dedicated dispatch team is available 24/7 to assist you with your shipment.
            </p>
            <a 
              href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '447XXXXXXXXX'}?text=Hi, I'm ${quote.contact_name} from ${quote.company_name}. Reference: ${quoteId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-[#25D366] hover:bg-[#25D366]/90 text-white font-semibold">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Contact RTS Dispatch
              </Button>
            </a>
            <p className="text-slate-500 text-xs mt-3">Fast response • Real-time updates • Professional support</p>
          </div>
        )}
      </main>
    </div>
  );
}
